/**
 * A JavaScript implementation of Verifiable Credentials.
 *
 * @author Dave Longley
 * @author David I. Lehn
 *
 * @license BSD 3-Clause License
 * Copyright (c) 2017-2019 Digital Bazaar, Inc.
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are permitted provided that the following conditions are met:
 *
 * Redistributions of source code must retain the above copyright notice,
 * this list of conditions and the following disclaimer.
 *
 * Redistributions in binary form must reproduce the above copyright
 * notice, this list of conditions and the following disclaimer in the
 * documentation and/or other materials provided with the distribution.
 *
 * Neither the name of the Digital Bazaar, Inc. nor the names of its
 * contributors may be used to endorse or promote products derived from
 * this software without specific prior written permission.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS
 * IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED
 * TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A
 * PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT
 * HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL,
 * SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED
 * TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR
 * PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF
 * LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING
 * NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS
 * SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 */
'use strict';

var _regeneratorRuntime = require("/Users/orie/Desktop/Transmute/vc-greeting-card/node_modules/@babel/runtime/regenerator");

var _defineProperty = require("/Users/orie/Desktop/Transmute/vc-greeting-card/node_modules/@babel/runtime/helpers/defineProperty");

var _asyncToGenerator = require("/Users/orie/Desktop/Transmute/vc-greeting-card/node_modules/@babel/runtime/helpers/asyncToGenerator");

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

var jsonld = require('jsonld');

var jsigs = require('jsonld-signatures');

var CredentialIssuancePurpose = require('./CredentialIssuancePurpose');

var defaultDocumentLoader = jsigs.extendContextLoader(require('./documentLoader')); // Z and T can be lowercase
// RFC3339 regex

var dateRegex = new RegExp('^(\\d{4})-(0[1-9]|1[0-2])-' + '(0[1-9]|[12][0-9]|3[01])T([01][0-9]|2[0-3]):' + '([0-5][0-9]):([0-5][0-9]|60)' + '(\\.[0-9]+)?(Z|(\\+|-)([01][0-9]|2[0-3]):' + '([0-5][0-9]))$', 'i');
module.exports = {
  issue: issue,
  verify: verify,
  createPresentation: createPresentation,
  CredentialIssuancePurpose: CredentialIssuancePurpose,
  defaultDocumentLoader: defaultDocumentLoader,
  // export for testing:
  _checkCredential: _checkCredential,
  _checkPresentation: _checkPresentation,
  _verifyPresentation: _verifyPresentation,
  dateRegex: dateRegex
};
/**
 * Issues a verifiable credential.
 *
 * @param {Object} [options={}]
 *
 * Required:
 * @param {Object} options.credential - Base credential document
 * @param {LinkedDataSignature} options.suite - passed in to sign()
 *
 * Either pass in a ProofPurpose, or a default one will be created:
 * @param {ProofPurpose} [options.purpose]
 * @param {string} [options.controller]
 * @param {string} [options.domain]
 *
 * @param {string|Date} [options.issuanceDate]
 *
 * Other optional params passed to `sign()`:
 * @param {function} [options.documentLoader]
 * @param {Object} [options.expansionMap]
 * @param {boolean} [options.compactProof]
 *
 * @throws {Error}
 *
 * @returns {Promise<object>}
 */

function issue() {
  return _issue.apply(this, arguments);
}
/**
 * Verifies a verifiable credential or verifiable presentation.
 *
 * @param {object} [options={}]
 *
 * Required, one of:
 * @param {object} [options.credential] - Verifiable credential
 * @param {object} [options.presentation] - Verifiable presentation
 *
 * @throws {TypeError} If neither credential or presentation is passed.
 * @throws {TypeError} If a verifiable presentation is passed as a credential.
 *
 * @param {CredentialIssuancePurpose} [options.purpose]
 * @param {function} [options.documentLoader]
 *
 * @returns {Promise<{verified: boolean, results: *[], error: Error}>}
 */


function _issue() {
  _issue = _asyncToGenerator(
  /*#__PURE__*/
  _regeneratorRuntime.mark(function _callee() {
    var options,
        documentLoader,
        controller,
        domain,
        purpose,
        credential,
        _args = arguments;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            options = _args.length > 0 && _args[0] !== undefined ? _args[0] : {};
            documentLoader = options.documentLoader || defaultDocumentLoader;
            controller = options.controller, domain = options.domain;
            purpose = options.purpose || new CredentialIssuancePurpose({
              controller: controller,
              domain: domain
            }); // run common credential checks

            credential = options.credential;

            if (credential) {
              _context.next = 7;
              break;
            }

            throw new TypeError('"credential" property is required for issuing.');

          case 7:
            _checkCredential(credential);

            return _context.abrupt("return", jsigs.sign(credential, _objectSpread({
              purpose: purpose,
              documentLoader: documentLoader
            }, options)));

          case 9:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  }));
  return _issue.apply(this, arguments);
}

function verify() {
  return _verify.apply(this, arguments);
}
/**
 * Verifies a verifiable credential.
 *
 * @private
 * @param {Object} [options={}]
 *
 * @param {Object} options.credential - Verifiable credential.
 * @throws {TypeError} If a VP is passed instead of a credential.
 *
 * @param {CredentialIssuancePurpose} [options.purpose]
 * @param {Function} [options.documentLoader]
 *
 * @returns {Promise<{verified: boolean, results: *[], error: Error}>}
 */


function _verify() {
  _verify = _asyncToGenerator(
  /*#__PURE__*/
  _regeneratorRuntime.mark(function _callee2() {
    var options,
        credential,
        presentation,
        _args2 = arguments;
    return _regeneratorRuntime.wrap(function _callee2$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            options = _args2.length > 0 && _args2[0] !== undefined ? _args2[0] : {};
            credential = options.credential, presentation = options.presentation;

            if (!(!credential && !presentation)) {
              _context2.next = 4;
              break;
            }

            throw new TypeError('A "credential" or "presentation" property is required for verifying.');

          case 4:
            if (!credential) {
              _context2.next = 6;
              break;
            }

            return _context2.abrupt("return", _verifyCredential(options));

          case 6:
            return _context2.abrupt("return", _verifyPresentation(options));

          case 7:
          case "end":
            return _context2.stop();
        }
      }
    }, _callee2);
  }));
  return _verify.apply(this, arguments);
}

function _verifyCredential() {
  return _verifyCredential2.apply(this, arguments);
}
/**
 * Creates a verifiable presentation from a given verifiable credential.
 *
 * @param {object} [options={}]
 *
 * Required:
 * @param {string|Array<string>} options.type - One or more specific
 *   presentation types (in addition to 'VerifiablePresentation').
 * @param {LinkedDataSignature} options.suite - passed in to sign()
 *
 * Either pass in a `presentation` or a `verifiableCredential`
 * @param {object} [options.presentation]
 * @param {object|Array<object>} [options.verifiableCredential] - One or more
 *   verifiable credentials
 *
 * Either pass in a ProofPurpose, or a default one will be created:
 * @param {ProofPurpose} [options.purpose]
 * @param {string} [options.controller]
 * @param {string} [options.domain]
 * @param {string} [options.challenge]
 *
 * Optional:
 * @param {string} [options.id] - Optional VP id
 * @param {function} [options.documentLoader]
 *
 * @returns {Promise<{object}>}
 */


function _verifyCredential2() {
  _verifyCredential2 = _asyncToGenerator(
  /*#__PURE__*/
  _regeneratorRuntime.mark(function _callee3() {
    var options,
        credential,
        documentLoader,
        controller,
        domain,
        purpose,
        _args3 = arguments;
    return _regeneratorRuntime.wrap(function _callee3$(_context3) {
      while (1) {
        switch (_context3.prev = _context3.next) {
          case 0:
            options = _args3.length > 0 && _args3[0] !== undefined ? _args3[0] : {};
            credential = options.credential; // check if developer mistakenly passed in a VP as 'credential'

            if (!jsonld.hasValue(credential, 'type', 'VerifiablePresentation')) {
              _context3.next = 4;
              break;
            }

            throw new TypeError('Use "presentation" property when verifying VPs.');

          case 4:
            _context3.prev = 4;

            _checkCredential(credential);

            _context3.next = 11;
            break;

          case 8:
            _context3.prev = 8;
            _context3.t0 = _context3["catch"](4);
            return _context3.abrupt("return", {
              verified: false,
              results: [{
                credential: credential,
                verified: false,
                error: _context3.t0
              }],
              error: _context3.t0
            });

          case 11:
            documentLoader = options.documentLoader || defaultDocumentLoader;
            controller = options.controller, domain = options.domain;
            purpose = options.purpose || new CredentialIssuancePurpose({
              controller: controller,
              domain: domain
            });
            return _context3.abrupt("return", jsigs.verify(credential, _objectSpread({
              purpose: purpose,
              documentLoader: documentLoader
            }, options)));

          case 15:
          case "end":
            return _context3.stop();
        }
      }
    }, _callee3, null, [[4, 8]]);
  }));
  return _verifyCredential2.apply(this, arguments);
}

function createPresentation() {
  return _createPresentation.apply(this, arguments);
}
/**
 * @param [options={}] - {object}.
 *
 * @param options.presentation - {object} VC Presentation.
 *
 * @returns {Promise<{verified: boolean, results: *[], error: Error}>}
 */


function _createPresentation() {
  _createPresentation = _asyncToGenerator(
  /*#__PURE__*/
  _regeneratorRuntime.mark(function _callee4() {
    var options,
        type,
        id,
        types,
        presentation,
        _iteratorNormalCompletion,
        _didIteratorError,
        _iteratorError,
        _iterator,
        _step,
        c,
        credential,
        documentLoader,
        controller,
        domain,
        challenge,
        purpose,
        _args4 = arguments;

    return _regeneratorRuntime.wrap(function _callee4$(_context4) {
      while (1) {
        switch (_context4.prev = _context4.next) {
          case 0:
            options = _args4.length > 0 && _args4[0] !== undefined ? _args4[0] : {};

            if (!(!options.presentation && !options.verifiableCredential)) {
              _context4.next = 3;
              break;
            }

            throw new TypeError('"presentation" or "verifiableCredential" parameter is required.');

          case 3:
            type = options.type, id = options.id;

            if (type) {
              _context4.next = 6;
              break;
            }

            throw new TypeError('A presentation requires one or more specific "type" values.');

          case 6:
            types = [].concat(type);

            if (!types.includes('VerifiablePresentation')) {
              types = ['VerifiablePresentation'].concat(types);
            }

            presentation = options.presentation || {
              '@context': options.verifiableCredential['@context'],
              type: types,
              verifiableCredential: [].concat(options.verifiableCredential)
            };

            if (id) {
              presentation.id = id;
            }

            _checkPresentation(presentation);

            _iteratorNormalCompletion = true;
            _didIteratorError = false;
            _iteratorError = undefined;
            _context4.prev = 14;

            for (_iterator = presentation.verifiableCredential[Symbol.iterator](); !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
              c = _step.value;
              credential = JSON.parse(JSON.stringify(c));
              credential['@context'] = presentation['@context'];

              _checkCredential(credential);
            }

            _context4.next = 22;
            break;

          case 18:
            _context4.prev = 18;
            _context4.t0 = _context4["catch"](14);
            _didIteratorError = true;
            _iteratorError = _context4.t0;

          case 22:
            _context4.prev = 22;
            _context4.prev = 23;

            if (!_iteratorNormalCompletion && _iterator.return != null) {
              _iterator.return();
            }

          case 25:
            _context4.prev = 25;

            if (!_didIteratorError) {
              _context4.next = 28;
              break;
            }

            throw _iteratorError;

          case 28:
            return _context4.finish(25);

          case 29:
            return _context4.finish(22);

          case 30:
            documentLoader = options.documentLoader || defaultDocumentLoader;
            controller = options.controller, domain = options.domain, challenge = options.challenge;
            purpose = options.purpose || new CredentialIssuancePurpose({
              controller: controller,
              domain: domain,
              challenge: challenge
            });
            return _context4.abrupt("return", jsigs.sign(presentation, _objectSpread({
              purpose: purpose,
              documentLoader: documentLoader
            }, options)));

          case 34:
          case "end":
            return _context4.stop();
        }
      }
    }, _callee4, null, [[14, 18, 22, 30], [23,, 25, 29]]);
  }));
  return _createPresentation.apply(this, arguments);
}

function _verifyPresentation() {
  return _verifyPresentation2.apply(this, arguments);
}

function _verifyPresentation2() {
  _verifyPresentation2 = _asyncToGenerator(
  /*#__PURE__*/
  _regeneratorRuntime.mark(function _callee5() {
    var options,
        presentation,
        documentLoader,
        credentialResult,
        allCredentialsVerified,
        controller,
        domain,
        challenge,
        purpose,
        presentationResult,
        _args5 = arguments;
    return _regeneratorRuntime.wrap(function _callee5$(_context5) {
      while (1) {
        switch (_context5.prev = _context5.next) {
          case 0:
            options = _args5.length > 0 && _args5[0] !== undefined ? _args5[0] : {};
            presentation = options.presentation;

            if (presentation) {
              _context5.next = 4;
              break;
            }

            throw new TypeError('"presentation" property is required for verifying.');

          case 4:
            _context5.prev = 4;

            _checkPresentation(presentation);

            _context5.next = 11;
            break;

          case 8:
            _context5.prev = 8;
            _context5.t0 = _context5["catch"](4);
            return _context5.abrupt("return", {
              verified: false,
              results: [{
                presentation: presentation,
                verified: false,
                error: _context5.t0
              }],
              error: _context5.t0
            });

          case 11:
            documentLoader = options.documentLoader || defaultDocumentLoader; // verify every credential in `verifiableCredential`

            _context5.next = 14;
            return Promise.all([].concat(presentation.verifiableCredential).map(function (c) {
              var credential = JSON.parse(JSON.stringify(c));
              credential['@context'] = presentation['@context'];
              return verify(_objectSpread({
                credential: credential,
                documentLoader: documentLoader
              }, options));
            }));

          case 14:
            credentialResult = _context5.sent;
            allCredentialsVerified = credentialResult.every(function (r) {
              return r.verified;
            });
            controller = options.controller, domain = options.domain, challenge = options.challenge;
            purpose = options.purpose || new CredentialIssuancePurpose({
              controller: controller,
              domain: domain,
              challenge: challenge
            });
            presentationResult = jsigs.verify(presentation, _objectSpread({
              purpose: purpose,
              documentLoader: documentLoader
            }, options));
            return _context5.abrupt("return", {
              verified: presentationResult.verified && allCredentialsVerified,
              results: [].concat([presentationResult, credentialResult]),
              error: presentationResult.error
            });

          case 20:
          case "end":
            return _context5.stop();
        }
      }
    }, _callee5, null, [[4, 8]]);
  }));
  return _verifyPresentation2.apply(this, arguments);
}

function _getId(obj) {
  if (typeof obj === 'string') {
    return obj;
  }

  if (!('id' in obj)) {
    throw new Error('"id" not found.');
  }

  return obj.id;
}
/**
 * @param presentation
 * @throws {Error}
 * @private
 */


function _checkPresentation(presentation) {
  // check context cardinality
  if (jsonld.getValues(presentation, '@context').length < 2) {
    throw new Error('"@context" property needs to be an array of two or more contexts.');
  } // ensure first context is 'https://www.w3.org/2018/credentials/v1'


  if (presentation['@context'][0] !== 'https://www.w3.org/2018/credentials/v1') {
    throw new Error('https://www.w3.org/2018/credentials/v1 needs to be first in the list of contexts.');
  } // check type presence and cardinality


  if (!presentation['type']) {
    throw new Error('"type" property is required.');
  }

  if (!Array.isArray(presentation['type']) || presentation['type'].length < 2 || !presentation['type'].includes('VerifiablePresentation')) {
    throw new Error('"type" must be `VerifiablePresentation` plus specific type.');
  }

  if (!presentation['verifiableCredential']) {
    throw new Error('"verifiableCredential" property is required.');
  }
}
/**
 * @param credential
 * @throws {Error}
 * @private
 */


function _checkCredential(credential) {
  // check context cardinality
  if (jsonld.getValues(credential, '@context').length < 2) {
    throw new Error('"@context" property needs to be an array of two or more contexts.');
  } // ensure first context is 'https://www.w3.org/2018/credentials/v1'


  if (credential['@context'][0] !== 'https://www.w3.org/2018/credentials/v1') {
    throw new Error('https://www.w3.org/2018/credentials/v1 needs to be first in the list of contexts.');
  } // check type presence and cardinality


  if (!credential['type']) {
    throw new Error('"type" property is required.');
  }

  if (!Array.isArray(credential['type']) || credential['type'].length < 2 || !credential['type'].includes('VerifiableCredential')) {
    throw new Error('"type" must be `VerifiableCredential` plus specific type.');
  }

  if (!credential['credentialSubject']) {
    throw new Error('"credentialSubject" property is required.');
  }

  if (!credential['issuer']) {
    throw new Error('"issuer" property is required.');
  } // check issuanceDate cardinality


  if (jsonld.getValues(credential, 'issuanceDate').length > 1) {
    throw new Error('"issuanceDate" property can only have one value.');
  } // check issued is a date


  if (!credential['issuanceDate']) {
    throw new Error('"issuanceDate" property is required.');
  }

  if ('issuanceDate' in credential) {
    if (!dateRegex.test(credential.issuanceDate)) {
      throw new Error("\"issuanceDate\" must be a valid date: ".concat(credential.issuanceDate));
    }
  } // check issuer cardinality


  if (jsonld.getValues(credential, 'issuer').length > 1) {
    throw new Error('"issuer" property can only have one value.');
  } // check issuer is a URL
  // FIXME


  if ('issuer' in credential) {
    var issuer = _getId(credential.issuer);

    if (!issuer.includes(':')) {
      throw new Error("\"issuer\" property must be a URL: ".concat(issuer));
    }
  }

  if ('credentialStatus' in credential) {
    if (!credential.credentialStatus.id) {
      throw new Error('"credentialStatus" must include an id.');
    }

    if (!credential.credentialStatus.type) {
      throw new Error('"credentialStatus" must include a type.');
    }
  } // check evidences are URLs
  // FIXME


  jsonld.getValues(credential, 'evidence').forEach(function (v) {
    var evidence = _getId(v);

    if (!evidence.includes(':')) {
      throw new Error("\"evidence\" property must be a URL: ".concat(evidence));
    }
  }); // check expires is a date

  if ('expirationDate' in credential && !dateRegex.test(credential.expirationDate)) {
    throw new Error("\"expirationDate\" must be a valid date: ".concat(credential.expirationDate));
  } // check revocations


  jsonld.getValues(credential, 'revocation').forEach(function (v) {
    // check ids are URLs
    // FIXME
    var revocation = _getId(v);

    if (!revocation.includes(':')) {
      throw new Error("\"revocation\" property must be a URL: ".concat(revocation));
    } // check types are valid


    var knownTypes = ['RevocationList2017'];
    jsonld.getValues(v, 'type').forEach(function (t) {
      if (!knownTypes.includes(t)) {
        throw new Error("Unknown revocation type: ".concat(t));
      }
    });
  });
}